/**
 * Mechanical Equipment Tool
 * 
 * Places mechanical equipment (e.g., AHU, VAV) with connectors.
 */

import { v4 as uuidv4 } from 'uuid';
import { MechanicalEquipmentElement, MechanicalEquipmentGeometryData, MEPConnector } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export class MechanicalEquipmentTool extends BaseTool {
    id = 'mechanical_equipment';
    name = 'Mechanical Equipment';
    cursor = 'crosshair';

    private currentPoint: THREE.Vector3 | null = null;
    private rotation: number = 0;

    // Default VAV Box dimensions
    private width = 600;
    private depth = 400;
    private height = 300;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        if (this.currentPoint) {
            this.createEquipment();
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        // Find host level or place on workplane
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === ' ') {
            // Spacebar to rotate
            this.rotation += Math.PI / 2;
            if (this.rotation >= Math.PI * 2) this.rotation = 0;
            this.updatePreview();
            event.preventDefault();
        } else if (event.key === 'Escape') {
            this.deactivate();
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (!this.currentPoint) {
            this.context.appStore.setToolPreview(null);
            return;
        }

        this.context.appStore.setToolPreview({
            type: 'box',
            position: this.currentPoint,
            size: [this.width / 1000, this.depth / 1000, this.height / 1000],
            rotation: [0, 0, this.rotation],
            color: '#888888'
        });
    }

    private createEquipment() {
        if (!this.context || !this.currentPoint) return;

        // Define connectors for a VAV Box
        // 1. Inlet (Round)
        // 2. Outlet (Rectangular)
        const connectors: MEPConnector[] = [
            {
                id: uuidv4(),
                position: [-0.3, 0, 0], // Relative to center
                direction: [-1, 0, 0],
                systemType: 'Supply Air',
                size: 200, // 200mm dia
                shape: 'Round'
            },
            {
                id: uuidv4(),
                position: [0.3, 0, 0], // Relative to center
                direction: [1, 0, 0],
                systemType: 'Supply Air',
                size: 300, // 300 width
                height: 200, // 200 height
                shape: 'Rectangular'
            }
        ];

        const geometryData: MechanicalEquipmentGeometryData = {
            position: [this.currentPoint.x, this.currentPoint.y, this.currentPoint.z + (this.height / 2000)], // Center Z
            rotation: this.rotation,
            width: this.width,
            depth: this.depth,
            height: this.height,
            connectors
        };

        const equipment: MechanicalEquipmentElement = {
            id: uuidv4(),
            category: 'Mechanical Equipment',
            type: 'VAV Box - Parallel Fan Powered',
            parameters: {
                'Mark': 'VAV-1',
                'Flow': '500 CFM',
                'Pressure Drop': '0.5 in.wg'
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(equipment);

        console.log('Created Mechanical Equipment with', connectors.length, 'connectors');
    }
}
